import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RefreshCw, AlertTriangle, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

/**
 * ScadenzeMismatchAlert — rileva automaticamente se le scadenze INPS in DB non
 * corrispondono al regime previdenziale attuale dell'utente e propone la sincronizzazione
 * con un click. Utile per utenti "legacy" che hanno dati stale.
 *
 * Compatto, non intrusivo: appare solo quando serve, sparisce dopo il sync.
 */
export default function ScadenzeMismatchAlert({ onSynced }) {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (!user || (user.tipo_piva !== "forfettario" && user.tipo_piva !== "ordinario")) {
      setInfo(null);
      return;
    }
    api
      .get("/auth/regime-info")
      .then((r) => setInfo(r.data))
      .catch(() => setInfo(null));
  }, [user?.codice_ateco, user?.cassa_previdenziale, user?.tipo_piva]);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/deadlines/seed-standard?force=true");
      toast.success(`Scadenze sincronizzate (${r.data.seeded} generate in base al tuo regime)`);
      setInfo((i) => (i ? { ...i, scadenze_mismatch: false } : null));
      if (onSynced) onSynced();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore sincronizzazione");
    } finally {
      setSyncing(false);
    }
  };

  if (dismissed || !info || !info.scadenze_mismatch) return null;

  return (
    <div
      className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 fade-in-up"
      data-testid="scadenze-mismatch-alert"
    >
      <div className="flex items-start gap-3 flex-1 min-w-0 w-full">
        <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-4 h-4 text-amber-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-amber-900" data-testid="mismatch-title">
            Alcune scadenze non corrispondono al tuo regime
          </div>
          <div className="text-xs text-amber-800 mt-0.5">
            Il tuo regime rilevato è <strong>{info.label}</strong>, ma ci sono scadenze INPS legacy da sincronizzare.
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 w-full sm:w-auto">
        <Button
          onClick={sync}
          disabled={syncing}
          size="sm"
          className="rounded-full h-9 bg-amber-700 hover:bg-amber-800 text-white flex-1 sm:flex-none"
          data-testid="mismatch-sync-btn"
        >
          {syncing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
          Sincronizza ora
        </Button>
        <button
          onClick={() => setDismissed(true)}
          className="p-1 rounded hover:bg-amber-100 transition-colors"
          data-testid="mismatch-dismiss"
          aria-label="Chiudi"
        >
          <X className="w-4 h-4 text-amber-700" />
        </button>
      </div>
    </div>
  );
}
